"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VALID_REGIONS = exports.SUPRANATIONAL_REGIONS = exports.COUNTRY_CODES = void 0;
/**
 * ISO 3166-1 alpha-2 country codes supported by Climatiq, paired with
 * their human-readable names. Sorted alphabetically by country name.
 *
 * Declared `as const` so each tuple's first element is a string
 * *literal* — that's what lets us derive the `CountryCode` union below
 * and have the compiler refuse any unknown two-letter code at every
 * call site (settings dropdowns, seed scripts, factor-search schemas).
 *
 * This is the single source of truth for valid country codes across
 * the emissions microservice; the `Region` union (countries +
 * supranationals) is the consumer-facing type.
 */
exports.COUNTRY_CODES = [
    ['AF', 'Afghanistan'],
    ['AL', 'Albania'],
    ['DZ', 'Algeria'],
    ['AR', 'Argentina'],
    ['AM', 'Armenia'],
    ['AU', 'Australia'],
    ['AT', 'Austria'],
    ['AZ', 'Azerbaijan'],
    ['BH', 'Bahrain'],
    ['BD', 'Bangladesh'],
    ['BE', 'Belgium'],
    ['BJ', 'Benin'],
    ['BO', 'Bolivia'],
    ['BA', 'Bosnia and Herzegovina'],
    ['BW', 'Botswana'],
    ['BR', 'Brazil'],
    ['BN', 'Brunei'],
    ['BG', 'Bulgaria'],
    ['CM', 'Cameroon'],
    ['CA', 'Canada'],
    ['CL', 'Chile'],
    ['CN', 'China'],
    ['CO', 'Colombia'],
    ['CD', 'Congo (DRC)'],
    ['CR', 'Costa Rica'],
    ['HR', 'Croatia'],
    ['CY', 'Cyprus'],
    ['CZ', 'Czech Republic'],
    ['DK', 'Denmark'],
    ['DO', 'Dominican Republic'],
    ['EC', 'Ecuador'],
    ['EG', 'Egypt'],
    ['SV', 'El Salvador'],
    ['EE', 'Estonia'],
    ['ET', 'Ethiopia'],
    ['FI', 'Finland'],
    ['FR', 'France'],
    ['GE', 'Georgia'],
    ['DE', 'Germany'],
    ['GH', 'Ghana'],
    ['GR', 'Greece'],
    ['GT', 'Guatemala'],
    ['HN', 'Honduras'],
    ['HK', 'Hong Kong'],
    ['HU', 'Hungary'],
    ['IS', 'Iceland'],
    ['IN', 'India'],
    ['ID', 'Indonesia'],
    ['IQ', 'Iraq'],
    ['IE', 'Ireland'],
    ['IL', 'Israel'],
    ['IT', 'Italy'],
    ['JP', 'Japan'],
    ['JO', 'Jordan'],
    ['KZ', 'Kazakhstan'],
    ['KE', 'Kenya'],
    ['KW', 'Kuwait'],
    ['KG', 'Kyrgyzstan'],
    ['LV', 'Latvia'],
    ['LB', 'Lebanon'],
    ['LT', 'Lithuania'],
    ['LU', 'Luxembourg'],
    ['MK', 'North Macedonia'],
    ['MY', 'Malaysia'],
    ['MX', 'Mexico'],
    ['MD', 'Moldova'],
    ['MN', 'Mongolia'],
    ['MA', 'Morocco'],
    ['MZ', 'Mozambique'],
    ['MM', 'Myanmar'],
    ['NP', 'Nepal'],
    ['NL', 'Netherlands'],
    ['NZ', 'New Zealand'],
    ['NG', 'Nigeria'],
    ['NO', 'Norway'],
    ['OM', 'Oman'],
    ['PK', 'Pakistan'],
    ['PA', 'Panama'],
    ['PY', 'Paraguay'],
    ['PE', 'Peru'],
    ['PH', 'Philippines'],
    ['PL', 'Poland'],
    ['PT', 'Portugal'],
    ['QA', 'Qatar'],
    ['RO', 'Romania'],
    ['RU', 'Russia'],
    ['SA', 'Saudi Arabia'],
    ['SN', 'Senegal'],
    ['RS', 'Serbia'],
    ['SG', 'Singapore'],
    ['SK', 'Slovakia'],
    ['SI', 'Slovenia'],
    ['ZA', 'South Africa'],
    ['KR', 'South Korea'],
    ['ES', 'Spain'],
    ['LK', 'Sri Lanka'],
    ['SE', 'Sweden'],
    ['CH', 'Switzerland'],
    ['TW', 'Taiwan'],
    ['TJ', 'Tajikistan'],
    ['TZ', 'Tanzania'],
    ['TH', 'Thailand'],
    ['TN', 'Tunisia'],
    ['TR', 'Turkey'],
    ['TM', 'Turkmenistan'],
    ['UG', 'Uganda'],
    ['UA', 'Ukraine'],
    ['AE', 'United Arab Emirates'],
    ['GB', 'United Kingdom'],
    ['US', 'United States'],
    ['UY', 'Uruguay'],
    ['UZ', 'Uzbekistan'],
    ['VE', 'Venezuela'],
    ['VN', 'Vietnam'],
    ['YE', 'Yemen'],
    ['ZM', 'Zambia'],
    ['ZW', 'Zimbabwe'],
];
/**
 * Supranational region sentinels recognised by Climatiq alongside the
 * ISO country codes.
 *   - `GLOBAL` — no region filter; the fallback when a (region, year)
 *     search returns nothing.
 *   - `EU` — Climatiq supranational code for European Union averages.
 */
exports.SUPRANATIONAL_REGIONS = ['GLOBAL', 'EU'];
/**
 * Single source of truth for valid region codes at *runtime*. Used by
 * model enum validation, zod validators and import-template dropdowns.
 *
 * The `as const` cast (followed by the type widen) keeps this a
 * runtime-iterable list while preserving `Region`-typed elements for
 * any consumer that does `VALID_REGIONS.includes(x)` etc.
 */
exports.VALID_REGIONS = [
    ...exports.COUNTRY_CODES.map(([code]) => code),
    ...exports.SUPRANATIONAL_REGIONS,
];
